/**
 * Hook-free runbook resolver, safe to import from server code (Q-299 P0 #3 follow-up).
 *
 * lib/routes/config.ts is imported by the server component app/template.tsx (through the
 * route registry), so it must not import lib/runbook/client.ts, which also exports React
 * hooks: `next build` refuses a server component whose import chain reaches useState/useEffect.
 * client.ts re-exports this function, so existing imports keep working.
 */

/**
 * Resolve a runbook namespace on the client side.
 *
 * Returns the override value from window.__RUNBOOK_CONFIG__ if available,
 * otherwise returns the provided default value.
 *
 * During SSR (typeof window === 'undefined'), always returns the default.
 * The RunbookConfig script tag in <head> ensures window.__RUNBOOK_CONFIG__
 * is set before React hydration, minimizing any visual flash.
 *
 * NOTE: When used inline in JSX with suppressHydrationWarning, React 18 may
 * keep the server-rendered text and never update it. Use useRunbook() instead
 * for text that must reflect the override after hydration.
 */
export function resolveRunbook(namespace: string, defaultValue: string): string {
  if (typeof window === 'undefined') return defaultValue;
  const config = window.__RUNBOOK_CONFIG__;
  if (!config || !(namespace in config)) return defaultValue;
  const value = config[namespace];
  return typeof value === 'string' ? value : defaultValue;
}
